import { type ComponentPropsWithRef, memo } from 'react';
import clsx from 'clsx';

import { Tag, TagType } from '@common/atoms/Tag';
import { WebpImage } from '@common/atoms/WebpImage';
import { type VideofeedItemPublicScope } from '@common/clients/api';
import { type ContextData } from '@common/defaults/ContextData';
import { WebpWidthEnum } from '@common/types/WebpWidth';
import { formatNumericDuration } from '@common/utils/DateTimeUtil';
import { getT, Ns } from '@web/i18n';
import styles from '@web/organisms/VideoCarousel/molecules/VideoPreview/VideoPreview.module.scss';

import { ProgressBar } from '../../atoms/ProgressBar';

export interface Props extends ComponentPropsWithRef<'li'> {
    video: VideofeedItemPublicScope;
    isActive?: boolean;
    isNext?: boolean;
    hideDuration?: boolean;
    contextData: ContextData;
}

const Component = ({ video, isActive, isNext, hideDuration, className, contextData, ...props }: Props) => {
    const __videos = getT(contextData.context.locale, Ns.Videos);

    return (
        <li
            {...props}
            className={clsx(
                styles['VideoPreview'],
                isActive && styles['active'],
                props.onClick && styles['clickable'],
                className,
            )}
        >
            {isActive ? (
                <Tag
                    className={clsx(styles['tag'], styles['active'])}
                    type={TagType.MEDIA}
                    data-testid="active-video-tag"
                    contextData={contextData}
                >{__videos`is-active`}</Tag>
            ) : null}
            {isNext ? (
                <Tag
                    className={clsx(styles['tag'], styles['next'])}
                    type={TagType.WHITE}
                    contextData={contextData}
                >{__videos`is-next`}</Tag>
            ) : null}
            <WebpImage
                src={video.thumbnail || video.preview || ''}
                alt={''}
                defaultSize={WebpWidthEnum.WIDTH_225}
                ratio={20 / 11}
            />
            <ProgressBar videoID={video.id.toString()} isActive={isActive === true} />
            {!hideDuration && video.duration ? (
                <Tag
                    className={clsx(styles['tag'], styles['duration'])}
                    type={TagType.TIME_STAMP}
                    contextData={contextData}
                >
                    {formatNumericDuration(video.duration)}
                </Tag>
            ) : null}
            <h4 className={styles['title']}>{video.domainTitle || video.title}</h4>
        </li>
    );
};

Component.displayName = 'VideoPreview';

export const VideoPreview = memo(Component);
